/**
 * <collection-filters-dropdown>
 *
 * Mobile "dropdown" filter layout: one or more toggle buttons, each opening a full-width panel inline under the
 * collection toolbar (Filters, and — when enabled — Sort). It behaves like a menu: only one panel is open at a
 * time, the close button / Escape / a second tap on the active toggle all close it.
 *
 * Each toggle is linked to its panel with aria-controls, so the component stays generic: adding another
 * toggle + panel pair needs no JS change. The panels' content (facets form, sort links) is the theme's own
 * markup, so filtering and sorting keep working exactly as they do elsewhere.
 */
class CollectionFiltersDropdown extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.openToggle = null;

    this.addEventListener('click', this.onClick.bind(this), { signal });
    document.addEventListener('keydown', this.onKeydown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get closeButton() {
    return this.querySelector('[data-filters-close]');
  }

  panelFor(toggle) {
    return toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : null;
  }

  onClick(event) {
    const toggle = event.target.closest('[data-dropdown-toggle]');

    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      this.openToggle === toggle ? this.close() : this.open(toggle);
      return;
    }

    if (event.target.closest('[data-filters-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.openToggle) {
      const toggle = this.openToggle;
      this.close();
      toggle.focus();
    }
  }

  setToggleState(toggle, open) {
    const panel = this.panelFor(toggle);
    if (panel) panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  open(toggle) {
    if (!this.panelFor(toggle)) return;

    // Collapse whichever panel is currently open so only one shows at a time.
    if (this.openToggle && this.openToggle !== toggle) {
      this.setToggleState(this.openToggle, false);
    }

    this.setToggleState(toggle, true);
    this.openToggle = toggle;
    this.classList.add('is-open');
    this.closeButton?.removeAttribute('hidden');
  }

  close() {
    if (this.openToggle) {
      this.setToggleState(this.openToggle, false);
    }

    this.openToggle = null;
    this.classList.remove('is-open');
    this.closeButton?.setAttribute('hidden', '');
  }
}

if (!window.customElements.get('collection-filters-dropdown')) {
  window.customElements.define('collection-filters-dropdown', CollectionFiltersDropdown);
}
